
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Verse } from "@/data/verses/types";

interface VerseDisplayProps {
  verses: Verse[];
}

export function VerseDisplay({ verses }: VerseDisplayProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Ensure the component scrolls to top when loaded
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentVerse = verses[currentIndex];

  const nextVerse = () => {
    setCurrentIndex((prev) => (prev + 1) % verses.length);
  };

  const prevVerse = () => {
    setCurrentIndex((prev) => (prev - 1 + verses.length) % verses.length);
  };

  if (!currentVerse) {
    return <div>No verses available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Verse Card */}
      <Card className="bg-[#151520]/80 backdrop-blur-sm border-[#333344] text-white">
        <CardContent className="p-6">
          <blockquote className="text-lg italic mb-4 text-center">
            "{currentVerse.text}"
          </blockquote>
          <p className="text-center font-medium text-blue-300">
            - {currentVerse.reference}
          </p>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button
          onClick={prevVerse}
          variant="outline"
          className="border-[#333344] bg-[#151520]/80 text-white hover:bg-[#333344]"
          disabled={verses.length <= 1}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        
        <span className="text-sm text-gray-400">
          {currentIndex + 1} of {verses.length}
        </span>
        
        <Button
          onClick={nextVerse}
          variant="outline"
          className="border-[#333344] bg-[#151520]/80 text-white hover:bg-[#333344]"
          disabled={verses.length <= 1}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Understanding Section */}
      <Card className="bg-[#151520]/80 backdrop-blur-sm border-[#333344] text-white">
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold mb-4 text-blue-300">Understanding</h3>
          <p className="text-gray-200 leading-relaxed">{currentVerse.why}</p>
        </CardContent>
      </Card>

      {/* Prayer Section */}
      {currentVerse.prayer && (
        <Card className="bg-[#151520]/80 backdrop-blur-sm border-[#333344] text-white">
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-4 text-blue-300">Prayer</h3>
            <p className="text-gray-200 leading-relaxed italic">{currentVerse.prayer}</p>
            <div className="mt-6 text-center">
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2"
                onClick={() => {
                  // Simple scroll to top after prayer
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Amen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
